import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPopupModalDetails } from "../../services/PopupModalService";

const DEFAULT_IMAGE = "/kuku-assets/images/slide 1 Authentic Taste.png";
const DEFAULT_TITLE = "Authentic Taste of Jamnagar";
const DEFAULT_DESCRIPTION =
  "Hand-crafted Jamnagari kachori, premium namkeen & sweets - delivered fresh to your door. Since 1965.";

const WelcomeModal = () => {
  const [show, setShow] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;
    let timerId;

    const loadModal = async () => {
      try {
        const popupData = await getPopupModalDetails();

        if (!isMounted || !popupData) {
          return;
        }

        setModalData(popupData);
        timerId = window.setTimeout(() => {
          if (isMounted) {
            setShow(true);
          }
        }, 700);
      } catch (error) {
        console.error("Failed to load homepage popup modal", error);
      }
    };

    loadModal();

    return () => {
      isMounted = false;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setShow(false);
      }
    };

    if (show) {
      window.addEventListener("keydown", onKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [show]);

  useEffect(() => {
    setImageIndex(0);
  }, [modalData]);

  const handleClose = () => setShow(false);

  if (!show || !modalData) {
    return null;
  }

  const title = modalData.header?.trim() || DEFAULT_TITLE;
  const description = modalData.description?.trim() || DEFAULT_DESCRIPTION;
  const imageCandidates = modalData.image_candidates?.length
    ? [...modalData.image_candidates, DEFAULT_IMAGE]
    : [DEFAULT_IMAGE];
  const imageUrl = imageCandidates[imageIndex] || DEFAULT_IMAGE;

  const handleImageError = () => {
    setImageIndex((currentIndex) => {
      const nextIndex = currentIndex + 1;
      return nextIndex < imageCandidates.length ? nextIndex : currentIndex;
    });
  };

  return (
    <>
      <div className="wm-backdrop" onClick={handleClose} aria-hidden="true" />

      <div
        className="wm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wmModalTitle"
      >
        <div className="wm-content">
          <div className="wm-header">
            <h5 className="wm-title" id="wmModalTitle">
              {title}
            </h5>
            <button
              type="button"
              className="wm-close"
              onClick={handleClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <img
            src={imageUrl}
            className="wm-img"
            alt={`${title} - Kuku Namkeen`}
            onError={handleImageError}
          />

          <div className="wm-body">
            <p className="wm-desc">{description}</p>
            <div className="wm-actions">
              <Link to="/categories" className="wm-btn-primary" onClick={handleClose}>
                Explore Products
              </Link>
              <button type="button" className="wm-btn-ghost" onClick={handleClose}>
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .wm-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          z-index: 1050;
          animation: wmFadeIn 0.25s ease;
        }

        @keyframes wmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .wm-dialog {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 1055;
          width: 92%;
          max-width: 800px;
          animation: wmSlideIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes wmSlideIn {
          from {
            transform: translate(-50%, -46%) scale(0.9);
            opacity: 0;
          }

          to {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        }

        .wm-content {
          background: #fff;
          border-radius: 0.5rem;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .wm-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.85rem 1.2rem;
          border-bottom: 1px solid #dee2e6;
          background-color: #fff;
        }

        .wm-title {
          font-size: 1.05rem;
          font-weight: 700;
          color: #c90281;
          margin: 0;
          font-family: "Poppins", sans-serif;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .wm-close {
          background: none;
          border: none;
          font-size: 1.6rem;
          line-height: 1;
          color: #333;
          cursor: pointer;
          padding: 0 0.2rem;
          opacity: 0.6;
          transition: opacity 0.2s;
        }

        .wm-close:hover {
          opacity: 1;
        }

        .wm-img {
          width: 100%;
          display: block;
          max-height: 340px;
          object-fit: contain;
          object-position: center;
        }

        .wm-body {
          padding: 1rem 1.2rem 1.4rem;
        }

        .wm-desc {
          font-size: 0.97rem;
          color: #555;
          margin: 0 0 1rem;
          line-height: 1.65;
          font-family: "Poppins", sans-serif;
        }

        .wm-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .wm-btn-primary {
          display: inline-block;
          background: #181a86;
          color: #fff;
          font-family: "Poppins", sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          padding: 0.55rem 1.6rem;
          border-radius: 50px;
          text-decoration: none;
          border: none;
          cursor: pointer;
          transition: background 0.25s, transform 0.2s;
        }

        .wm-btn-primary:hover {
          background: #c90281;
          color: #fff;
          transform: translateY(-2px);
          text-decoration: none;
        }

        .wm-btn-ghost {
          background: none;
          border: 1.5px solid #ccc;
          color: #888;
          font-family: "Poppins", sans-serif;
          font-size: 0.88rem;
          padding: 0.5rem 1.2rem;
          border-radius: 50px;
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s;
        }

        .wm-btn-ghost:hover {
          border-color: #c90281;
          color: #c90281;
        }

        @media (max-width: 480px) {
          .wm-img {
            max-height: 200px;
          }

          .wm-title {
            font-size: 0.92rem;
          }

          .wm-body {
            padding: 0.85rem 1rem 1.1rem;
          }
        }
      `}</style>
    </>
  );
};

export default WelcomeModal;
